import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";
import { useIntl } from "react-intl";

const NotificationContext = createContext(null);

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const intl = useIntl();
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showNotification = useCallback((message, severity = "info") => {
    setNotification({ open: true, message, severity });
  }, []);

  const translateApiError = useCallback((msg) => {
    if (!msg) return "Error occurred.";
    const key = `error.api.${msg}`;
    if (intl.messages[key]) {
      return intl.formatMessage({ id: key });
    }
    return msg;
  }, [intl]);

  const notify = {
    success: (msg) => showNotification(msg, "success"),
    error: (msg) => showNotification(msg, "error"),
    warning: (msg) => showNotification(msg, "warning"),
    info: (msg) => showNotification(msg, "info"),
    apiError: (err) => {
      console.error("API ERROR:", err);
      if (!err) return showNotification("Unknown error occurred.", "error");
      if (err.response?.data) {
        const { message, errorMessage } = err.response.data;
        return showNotification(translateApiError(message ?? errorMessage), "error");
      }
      if (err.message === "Network Error") {
        return showNotification("ネットワーク接続に問題があります。", "error");
      }
      return showNotification(translateApiError(err.message), "error");
    },
  };

  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={notification.severity}
          sx={{ width: "100%" }}
          onClose={() => setNotification((n) => ({ ...n, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
